"use client";

import Image from "next/image";
import { type JSX } from "react";
import { useTranslations } from "next-intl";
import { useSitecore } from "@sitecore-content-sdk/nextjs";

import { useLocationContext } from "contexts/LocationContext";

import RichTextWithConsent from "../../../atoms/RichTextWithConsent";
import RichTextWithTable from "../../../molecules/RichTextWithTable/RichTextWithTables";
import {
  getImageURLForCHPages,
  getLinkTargetValue,
} from "../../../utils/contentHubComponentsUtils";

export const Default = (): JSX.Element => {
  console.log("---->[LocationDetails] Component");

  const { page } = useSitecore();
  const t = useTranslations();
  const websiteDetailsPrefixText = t.has("location-website-details-prefix")
    ? t("location-website-details-prefix")
    : "Visit our website:";

  const locationDetailsStyles = `
  .location-icon svg{
    margin-left:0px !important;
    margin-block-start: 0px;
    margin-block-end: opx
  }
  
  `;

  const { location } = useLocationContext();

  console.log("---->[LocationDetails] location", location);

  const renderWebsiteField = () => {
    const websiteAddress =
      location?.LocationWebsite?.startsWith("http") ||
      location?.LocationWebsite?.startsWith("/")
        ? location?.LocationWebsite
        : "https://" + location?.LocationWebsite;
    return (
      <div className="location-icon text-gray flex gap-4 items-center mb-6">
        <span className="flex items-center">
          <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
            <div className="m-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM18.92 8H15.97C15.65 6.75 15.19 5.55 14.59 4.44C16.43 5.07 17.96 6.35 18.92 8ZM12 4.04C12.83 5.24 13.48 6.57 13.91 8H10.09C10.52 6.57 11.17 5.24 12 4.04ZM4.26 14C4.1 13.36 4 12.69 4 12C4 11.31 4.1 10.64 4.26 10H7.64C7.56 10.66 7.5 11.32 7.5 12C7.5 12.68 7.56 13.34 7.64 14H4.26ZM5.08 16H8.03C8.35 17.25 8.81 18.45 9.41 19.56C7.57 18.93 6.04 17.66 5.08 16ZM8.03 8H5.08C6.04 6.34 7.57 5.07 9.41 4.44C8.81 5.55 8.35 6.75 8.03 8ZM12 19.96C11.17 18.76 10.52 17.43 10.09 16H13.91C13.48 17.43 12.83 18.76 12 19.96ZM14.34 14H9.66C9.57 13.34 9.5 12.68 9.5 12C9.5 11.32 9.57 10.65 9.66 10H14.34C14.43 10.65 14.5 11.32 14.5 12C14.5 12.68 14.43 13.34 14.34 14ZM14.59 19.56C15.19 18.45 15.65 17.25 15.97 16H18.92C17.96 17.65 16.43 18.93 14.59 19.56ZM16.36 14C16.44 13.34 16.5 12.68 16.5 12C16.5 11.32 16.44 10.66 16.36 10H19.74C19.9 10.64 20 11.31 20 12C20 12.69 19.9 13.36 19.74 14H16.36Z"
                  fill="#257FBA"
                />
              </svg>
            </div>
          </div>
        </span>
        <div className="flex flex-wrap wrap-anywhere font-normal">
          <span className="text-text-gray text-base">
            {websiteDetailsPrefixText}
          </span>
          &nbsp;
          <a
            href={websiteAddress}
            target={getLinkTargetValue(websiteAddress)}
            className="text-base text-text-secondary-500 underline active:text-dark-blue hover:text-dark-blue active:no-underline"
          >
            {location?.LocationWebsite}
          </a>
        </div>
      </div>
    );
  };

  if (page.mode.isEditing) {
    return (
      <div
        className="article-details ch-details mx-auto bg-white w-full ml-5 mt-10"
        data-role="location-details-component"
      >
        <h1 className="text-ecolab-blue text-[28px] leading-[42px] tracking-[0.4px] font-medium text-black lg:text-[32px] lg:leading-[48px] lg:tracking-[0.8px] mt-5 mb-2">
          Location Component in Editing mode
        </h1>
      </div>
    );
  }
  if (!location) {
    return <></>;
  }
  const locationImageUrl = getImageURLForCHPages(
    location?.LocationImage?.results?.[0]?.assetToPublicLink?.results,
  );

  return (
    <>
      <style>{locationDetailsStyles}</style>
      <div
        className="article-details ch-details mx-auto bg-white w-full m-0 lg:w-[var(--article-container-width)] max-lg:px-4"
        data-role="location-details-component"
      >
        <div>
          <div className="mt-[40px]">
            {location?.LocationName && (
              <h1 className="text-ecolab-blue text-[24px] leading-[36px] tracking-[0.4px] font-normal lg:text-[32px] lg:leading-[40px] lg:tracking-[0.8px] mt-5 mb-3 after:content-[''] after:block after:clear-both after:pt-[12px] after:w-full lg:after:w-[400px] after:border-b-2 after:border-teal">
                {location?.LocationName}
              </h1>
            )}
            {location?.LocationCityAndCountry && (
              <h2 className="text-[22px] lg:text-2xl font-normal text-charcoal-black leading-[24px] tracking-[0.4px] lg:leading-[36px] lg:tracking-[0.8px] mt-0 mb-6">
                {location?.LocationCityAndCountry}
              </h2>
            )}
            {locationImageUrl && (
              <div className="w-full mb-6 object-contain">
                <Image
                  src={locationImageUrl}
                  alt={"Location middle image"}
                  width={875}
                  height={493}
                  className="rounded-md max-h-full w-auto h-auto max-w-full"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            )}

            {location?.LocationAddress && (
              <div className="location-icon font-normal text-gray space-y-1 flex flex-row gap-4 wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
                  <div className="m-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12ZM18 10.2C18 6.57 15.35 4 12 4C8.65 4 6 6.57 6 10.2C6 12.54 7.95 15.64 12 19.34C16.05 15.64 18 12.54 18 10.2ZM12 2C16.2 2 20 5.22 20 10.2C20 13.52 17.33 17.45 12 22C6.67 17.45 4 13.52 4 10.2C4 5.22 7.8 2 12 2Z"
                        fill="#257FBA"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: location?.LocationAddress,
                  }}
                ></div>
              </div>
            )}

            <hr className="mb-6! mt-0! border-t border-gray-horizontal-line!" />
            {location?.CustomerServiceInformation && (
              <div className="location-icon font-normal text-charcoal-black space-y-1 flex flex-row gap-4 wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
                  <div className="m-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21 12.22C21 6.73 16.74 3 12 3C7.31 3 3 6.65 3 12.28C2.4 12.62 2 13.26 2 14V16C2 17.1 2.9 18 4 18H5V11.9C5 8.03 8.13 4.9 12 4.9C15.87 4.9 19 8.03 19 11.9V19H11V21H19C20.1 21 21 20.1 21 19V17.78C21.59 17.47 22 16.86 22 16.14V13.84C22 13.14 21.59 12.53 21 12.22Z"
                        fill="#257FBA"
                      />
                      <path
                        d="M9 14C9.55228 14 10 13.5523 10 13C10 12.4477 9.55228 12 9 12C8.44772 12 8 12.4477 8 13C8 13.5523 8.44772 14 9 14Z"
                        fill="#257FBA"
                      />
                      <path
                        d="M15 14C15.5523 14 16 13.5523 16 13C16 12.4477 15.5523 12 15 12C14.4477 12 14 12.4477 14 13C14 13.5523 14.4477 14 15 14Z"
                        fill="#257FBA"
                      />
                      <path
                        d="M18 11.03C17.52 8.18 15.04 6 12.05 6C9.02 6 5.76 8.51 6.02 12.45C8.49 11.44 10.35 9.24 10.88 6.56C12.19 9.19 14.88 11 18 11.03Z"
                        fill="#257FBA"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: location?.CustomerServiceInformation,
                  }}
                ></div>
              </div>
            )}

            {location?.LocationHours && (
              <div className="location-icon font-normal text-charcoal-black space-y-1 flex flex-row gap-4 wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
                  <div className="m-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12.4996 8.09479H10.9996V14.0948L15.7496 16.9448L16.4996 15.7148L12.4996 13.3448V8.09479ZM17.3366 1.90479L21.9436 5.74979L20.6636 7.28479L16.0536 3.44179L17.3366 1.90479ZM6.66264 1.90479L7.94464 3.44079L3.33664 7.28479L2.05664 5.74879L6.66264 1.90479ZM11.9996 4.09479C7.02964 4.09479 2.99964 8.12479 2.99964 13.0948C2.99964 18.0648 7.02964 22.0948 11.9996 22.0948C16.9696 22.0948 20.9996 18.0648 20.9996 13.0948C20.9996 8.12479 16.9696 4.09479 11.9996 4.09479ZM11.9996 20.0948C8.13964 20.0948 4.99964 16.9548 4.99964 13.0948C4.99964 9.23479 8.13964 6.09479 11.9996 6.09479C15.8596 6.09479 18.9996 9.23479 18.9996 13.0948C18.9996 16.9548 15.8596 20.0948 11.9996 20.0948Z"
                        fill="#257FBA"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: location?.LocationHours,
                  }}
                ></div>
              </div>
            )}

            {location?.LocationPhoneNumberFax && (
              <div className="location-icon font-normal text-gray space-y-1 flex flex-row gap-4 wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
                  <div className="m-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6.54 5C6.6 5.89 6.75 6.76 6.99 7.59L5.79 8.79C5.38 7.59 5.12 6.32 5.03 5H6.54ZM16.4 17.02C17.25 17.26 18.12 17.41 19 17.47V18.96C17.68 18.87 16.41 18.61 15.2 18.21L16.4 17.02ZM7.5 3H4C3.45 3 3 3.45 3 4C3 13.39 10.61 21 20 21C20.55 21 21 20.55 21 20V16.51C21 15.96 20.55 15.51 20 15.51C18.76 15.51 17.55 15.31 16.43 14.94C16.33 14.9 16.22 14.89 16.12 14.89C15.86 14.89 15.61 14.99 15.41 15.18L13.21 17.38C10.38 15.93 8.06 13.62 6.62 10.79L8.82 8.59C9.1 8.31 9.18 7.92 9.07 7.57C8.7 6.45 8.5 5.25 8.5 4C8.5 3.45 8.05 3 7.5 3Z"
                        fill="#257FBA"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: location?.LocationPhoneNumberFax,
                  }}
                ></div>
              </div>
            )}

            {location?.LocationEmail && (
              <div className="location-icon font-normal text-charcoal-black space-y-1 flex flex-row gap-4 items-center wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div className="w-10 h-10 bg-gray-icon rounded-full flex items-center justify-center">
                  <div className="m-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L12 13L20 8V18ZM12 11L4 6H20L12 11Z"
                        fill="#257FBA"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: location?.LocationEmail }}
                ></div>
              </div>
            )}

            {location?.LocationWebsite && renderWebsiteField()}

            {location?.LocationAdditionalDetails && (
              <RichTextWithTable
                field={{ value: location?.LocationAdditionalDetails }}
                className="font-normal text-gray space-y-1 wrap-anywhere mb-[24px] richtext-content tracking-[0.4px] lg:tracking-[0.8px]"
              />
            )}

            {location?.LegacyHtml && (
              <>
                {location.LegacyHtml?.includes("ecolab-youtube-video") ? (
                  <RichTextWithConsent
                    htmlContent={location?.LegacyHtml}
                    className="ch-legacy-content"
                  />
                ) : (
                  <RichTextWithTable
                    field={{ value: location.LegacyHtml }}
                    className="text-[var(--text-base)] font-normal text-gray leading-[24px] tracking-[0.4px] lg:tracking-[0.8px] space-y-1 wrap-anywhere mb-5 richtext-content ch-legacy-content"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
